import {
  ButtonHTMLAttributes,
  CSSProperties,
  forwardRef,
  Fragment,
  HTMLAttributeAnchorTarget,
  MouseEvent,
  ReactNode,
} from 'react';

import styled from 'styled-components';

import { Rotate, RotateProps } from '@components/animators/Rotate';
import { Link } from '@components/link';
import { theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';
import { transitionFast } from '@utils/transitions';

import { Tooltip } from '../tooltip';

type IconButtonBgVariant = 'blue' | 'white' | 'secondary' | 'ghost' | 'aiGradient';
type IconButtonColorVariant =
  | 'blue'
  | 'darkgreen'
  | 'darkred'
  | 'green'
  | 'orange'
  | 'purple'
  | 'white'
  | 'red';
type IconButtonVariant =
  | `${IconButtonBgVariant}-${IconButtonColorVariant}`
  | 'secondary-danger'
  | 'ghost-danger';

export type IconButtonProps = ButtonHTMLAttributes<HTMLButtonElement> &
  RotateProps & {
    icon: ReactNode;

    rounded?: boolean;
    tooltip?: string;
    variant?: IconButtonVariant;
    dataTestid?: string;
    href?: string;
    target?: HTMLAttributeAnchorTarget;
    cursor?: CSSProperties['cursor'];
    stopPropagation?: boolean;
    preventDefault?: boolean;
    size?: 'sm' | 'md' | 'lg';
  };

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>((props, ref) => {
  const {
    href,
    target,
    icon,
    tooltip,
    variant = 'secondary-purple',
    dataTestid,
    rounded = false,
    cursor = 'pointer',
    onClick,
    stopPropagation = false,
    preventDefault = false,
    size = 'lg',
    isRotated,
    degrees,
    ...rest
  } = props;
  const [bgVariant, colorVariant] = variant.split('-');
  const IconWrapper = isRotated === undefined ? Fragment : StyledRotate;
  const wrapperProps = isRotated === undefined ? {} : { isRotated, degrees };

  return (
    <Link to={href} target={target}>
      <Tooltip content={tooltip}>
        <StyledIconButton
          type="button"
          data-color={colorVariant}
          data-variant={bgVariant}
          data-testid={dataTestid}
          data-rounded={rounded}
          data-cursor={cursor}
          data-size={size}
          ref={ref}
          onClick={(event: MouseEvent<HTMLButtonElement>) => {
            stopPropagation && event.stopPropagation();
            preventDefault && event.preventDefault();
            onClick?.(event);
          }}
          {...rest}
        >
          <IconWrapper {...wrapperProps}>{icon}</IconWrapper>
        </StyledIconButton>
      </Tooltip>
    </Link>
  );
});

const StyledRotate = styled(Rotate)({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
});

const StyledIconButton = styled.button({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  transition: transitionFast('background-color', 'color', 'transform'),
  borderRadius: pxToRem(8),
  border: 'none',
  padding: 0,
  outline: 'none',
  flexShrink: 0,
  '--shadow-focus-color': theme.color.purple50,
  color: theme.color.buttonIconPositive,

  '&[data-size="sm"]': {
    height: pxToRem(24),
    width: pxToRem(24),
    borderRadius: pxToRem(6),
  },
  '&[data-size="md"]': {
    height: pxToRem(32),
    width: pxToRem(32),
    borderRadius: pxToRem(8),
  },
  '&[data-size="lg"]': {
    height: pxToRem(40),
    width: pxToRem(40),
    borderRadius: pxToRem(8),
  },

  '&[data-rounded="true"]': {
    borderRadius: '50%',
  },

  '&[data-variant="white"]': {
    backgroundColor: theme.color.backgroundLightDark,
  },

  '&[data-variant="secondary"]': {
    backgroundColor: theme.color.uiTrans5,
  },

  '&[data-variant="blue"]': {
    backgroundColor: theme.color.blue100,
  },

  '&[data-variant="ghost"]': {
    background: 'transparent',
  },

  '&[data-variant="aiGradient"]': {
    background: theme.gradient.ai,
  },

  '&[data-color="blue"]': { color: theme.color.elementBlue100 },
  '&[data-color="danger"]': { color: theme.color.buttonIconPositive },
  '&[data-color="darkgreen"]': { color: theme.color.green200 },
  '&[data-color="darkred"]': { color: theme.color.red200 },
  '&[data-color="green"]': { color: theme.color.green100 },
  '&[data-color="orange"]': { color: theme.color.orange100 },
  '&[data-color="purple"]': { color: theme.color.buttonIconPositive },
  '&[data-color="red"]': { color: theme.color.red100 },
  '&[data-color="white"]': { color: theme.color.buttonIconNegative },
  '&[data-color="white"]&[data-size="sm"]': { color: theme.color.white100 },

  '&:disabled': {
    opacity: '0.5',
    cursor: 'not-allowed',
  },

  '&:not(:disabled)': {
    cursor: 'pointer',
    '&[data-cursor="grab"]': {
      cursor: 'grab',
    },

    '&:hover, &:active': {
      '&[data-color="purple"]': {
        color: theme.color.buttonIconPositiveHover,
      },

      '&[data-variant="secondary"]': {
        backgroundColor: theme.color.uiTrans10,
      },

      '&[data-variant="ghost"]': {
        backgroundColor: theme.color.uiTrans10,
      },
      '&[data-variant="blue"]': {
        backgroundColor: theme.color.blue200,
      },
      '&[data-color="white"]': {
        color: theme.color.white100,
      },
      '&[data-color="danger"]': {
        color: theme.color.red100,
        backgroundColor: theme.color.redTrans10,
      },
    },

    '&:focus-visible': {
      '&[data-color="danger"]': {
        color: theme.color.red100,
        backgroundColor: theme.color.redTrans10,
      },
    },

    '&:active': {
      transform: 'scale(0.95)',
    },

    '[data-headlessui-focus-visible] &:focus-visible': {
      boxShadow: theme.shadow.focus,
    },
  },
});
